/* eslint-disable no-unused-vars */
import saveBtn from "assets/pictures/awesome-save.svg";
import { Input } from "components/forms";
import { Field, FormikProvider, useFormik } from "formik";
import { Fragment, useState } from "react";
import { mutate } from "swr";
import { API_ENDPOINTS, request } from "utilities";
import notification from "utilities/notification";
import { toJson } from "utilities/str";
import * as Yup from "yup";
import 'bootstrap-icons/font/bootstrap-icons.css';

const AddressForm = ({ editId, data, handleClose }) => {
  const data_address = toJson(data?.address);

  const titleOptions = [
    { value: "", label: "Select Title" },
    { value: "Mr", label: "Mr" },
    { value: "Mrs", label: "Mrs" },
    { value: "Ms", label: "Ms" },
    { value: "Dr", label: "Dr" },
  ];

  const addressTypeOptions = [
    { value: "", label: "Select Address Type" },
    { value: "Home", label: "Home" },
    { value: "Work", label: "Work" },
    { value: "Billing", label: "Billing" },
    { value: "Shipping", label: "Shipping" },
  ];

  const [initialValues] = useState({
    title: data?.title || "",
    type: data?.type || "",
    default: true,
    address: data_address?.address || "",
    country: data_address?.country || "",
    state: data_address?.state || "",
    city: data_address?.city || "",
    zip: data_address?.zip || "",
    first_name: data?.first_name || "",
    last_name: data?.last_name || "",
    phone: data_address?.phone || "",
    email: data_address?.email || "",
  });

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    type: Yup.string().required("Address Type is required"),
    first_name: Yup.string()
      .required("First Name is required")
      .matches(/^[A-Za-z\s]+$/, "First Name must contain only letters"),
    last_name: Yup.string()
      .required("Last Name is required")
      .matches(/^[A-Za-z\s]+$/, "Last Name must contain only letters"),
    address: Yup.string().required("Address is required"),
    country: Yup.string().required("Country is required"),
    state: Yup.string().required("State is required"),
    city: Yup.string().required("City is required"),
    zip: Yup.string()
      .required("Pincode is required")
      .matches(/^\d{5,6}$/, "Pincode must be 5 or 6 digits"),
    phone: Yup.string()
      .required("Phone number is required")
      .matches(/^\d{10}$/, "Phone number must be 10 digits"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
  });

  const onSubmit = (values, { setSubmitting }) => {
    const url = data?.id
      ? `${API_ENDPOINTS.ADDRESS}/edit/${data?.id}`
      : API_ENDPOINTS.ADDRESS;
    request
      .post(url, values)
      .then((response) => {
        if (response?.success) {
          notification("success", response?.message);
          mutate(API_ENDPOINTS.ADDRESS);
          if (data?.id) {
            mutate(`${API_ENDPOINTS.ADDRESS}/${data?.id}`);
          }
          formik.handleReset();
          handleClose();
        }
      })
      .catch((error) => {
        notification("error", error?.message || "Failed to save address");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit,
  });

  return (
    <Fragment>
      <FormikProvider value={formik}>
        <form className="checkout-form" onSubmit={formik.handleSubmit}>
          {/* Row 1: Title and Address Type */}
          <div className="row">
            <div className="col-12 col-md-6 form-group">
              <div className="input-icons">
              <i className="bi bi-person-fill input-icon" />
                <Field name="title">
                  {({ field, meta }) => (
                    <div>
                      <select
                        {...field}
                        id="title"
                        className={`form-select fc-l checkbr ${
                          meta.touched && meta.error ? "is-invalid" : ""
                        }`}
                      >
                        {titleOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {meta.touched && meta.error && (
                        <div className="invalid-feedback">{meta.error}</div>
                      )}
                    </div>
                  )}
                </Field>
              </div>
            </div>
            <div className="col-12 col-md-6 form-group">
              <div className="input-icons">
              <i className="bi bi-geo-alt-fill input-icon" />
                <Field name="type">
                  {({ field, meta }) => (
                    <div>
                      <select
                        {...field}
                        id="type"
                        className={`form-select fc-l checkbr ${
                          meta.touched && meta.error ? "is-invalid" : ""
                        }`}
                      >
                        {addressTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {meta.touched && meta.error && (
                        <div className="invalid-feedback">{meta.error}</div>
                      )}
                    </div>
                  )}
                </Field>
              </div>
            </div>
          </div>

          {/* Row 2: First Name and Last Name */}
          <div className="row">
            <div className="col-12 col-md-6 form-group">
              <Field
                label="First Name"
                type="text"
                name="first_name"
                icon={() => <i className="bi bi-person" />}
                variant="2"
                component={Input}
              />
            </div>
            <div className="col-12 col-md-6 form-group">
              <Field
                label="Last Name"
                type="text"
                name="last_name"
                icon={() => <i className="bi bi-person" />}
                variant="2"
                component={Input}
              />
            </div>
          </div>

          {/* Row 3: Address */}
          <div className="row">
            <div className="col-12 form-group">
              <Field
                label="Address"
                type="text"
                name="address"
                icon={() => <i className="bi bi-house" />}
                variant="2"
                component={Input}
              />
            </div>
          </div>

          {/* Row 4: Country, City, State, Zip */}
          <div className="row g-3">
            <div className="col-12 col-md-3 form-group">
              <Field
                label="Country"
                type="text"
                name="country"
                icon={() =>  <i className="bi bi-globe" />}
                variant="2"
                component={Input}
              />
            </div>
            <div className="col-12 col-md-3 form-group">
              <Field
                label="City"
                type="text"
                name="city"
                icon={() => <i className="bi bi-buildings" />}
                variant="2"
                component={Input}
              />
            </div>
            <div className="col-12 col-md-3 form-group">
              <Field
                label="State"
                type="text"
                name="state"
                icon={() => <i className="bi bi-geo-alt" />}
                variant="2"
                component={Input}
              />
            </div>
            <div className="col-12 col-md-3 form-group">
              <Field
                label="Pincode"
                type="text"
                name="zip"
                icon={() => <i className="bi bi-mailbox" />}
                variant="2"
                component={Input}
              />
            </div>
          </div>

          {/* Row 5: Phone and Email */}
          <div className="row g-3">
            <div className="col-12 col-md-6 form-group">
              <Field
                label="Phone"
                type="tel"
                name="phone"
                icon={() => <i className="bi bi-telephone" />}
                variant="2"
                component={Input}
              />
            </div>
            <div className="col-12 col-md-6 form-group">
              <Field
                label="Email"
                type="email"
                name="email"
                icon={() => <i className="bi bi-mailbox" />}
                variant="2"
                component={Input}
              />
            </div>
          </div>

          {/* Submit */}
          <div className="submit-section mt-4">
            <button
              type="submit"
              className="save-address-btn"
              disabled={formik.isSubmitting}
            >
              <span className="sa-add">
                <img src={saveBtn} alt="Save" className="save-icon" /> Save
                Address
              </span>
            </button>
          </div>
        </form>
      </FormikProvider>
    </Fragment>
  );
};

export default AddressForm;
